import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FilterIcon, DownloadIcon, EyeIcon, EditIcon, TrashIcon, CheckCircleIcon, XCircleIcon, SearchIcon, ClockIcon, AlertCircleIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
// Mock data for campaigns
const campaignsData = [{
  id: 'CMP-2023-001',
  name: 'Summer Sale 2023',
  code: 'SUMMER23',
  status: 'active',
  type: 'Percentage',
  value: '15%',
  startDate: '2023-06-01T00:00:00Z',
  endDate: '2023-08-31T23:59:59Z',
  createdBy: 'maker@example.com',
  approvedBy: 'checker@example.com',
  budget: 5000000,
  utilized: 2450000
}, {
  id: 'CMP-2023-002',
  name: 'Eid Special Discount',
  code: 'EID23',
  status: 'expired',
  type: 'Fixed Amount',
  value: 'PKR 500',
  startDate: '2023-04-20T00:00:00Z',
  endDate: '2023-04-25T23:59:59Z',
  createdBy: 'maker@example.com',
  approvedBy: 'checker@example.com',
  budget: 2000000,
  utilized: 1950000
}, {
  id: 'CMP-2023-003',
  name: 'Tech Week Bonanza',
  code: 'TECHWK',
  status: 'pending',
  type: 'Percentage',
  value: '10%',
  startDate: '2023-11-15T00:00:00Z',
  endDate: '2023-11-22T23:59:59Z',
  createdBy: 'maker@example.com',
  approvedBy: null,
  budget: 1000000,
  utilized: 0
}, {
  id: 'CMP-2023-004',
  name: 'Winter Clearance',
  code: 'WINTER23',
  status: 'draft',
  type: 'Percentage',
  value: '25%',
  startDate: '2023-12-01T00:00:00Z',
  endDate: '2024-01-31T23:59:59Z',
  createdBy: 'maker@example.com',
  approvedBy: null,
  budget: 3000000,
  utilized: 0
}, {
  id: 'CMP-2023-005',
  name: 'Flash Sale Friday',
  code: 'FLASHFRI',
  status: 'rejected',
  type: 'Fixed Amount',
  value: 'PKR 1000',
  startDate: '2023-10-27T00:00:00Z',
  endDate: '2023-10-27T23:59:59Z',
  createdBy: 'maker@example.com',
  approvedBy: null,
  budget: 500000,
  utilized: 0,
  rejectionReason: 'Budget exceeds quarterly limit'
}];
interface Campaign {
  id: string;
  name: string;
  code: string;
  status: string;
  type: string;
  value: string;
  startDate: string;
  endDate: string;
  createdBy: string;
  approvedBy: string | null;
  budget: number;
  utilized: number;
  rejectionReason?: string;
}
const CampaignList: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>(campaignsData);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const handleCreateCampaign = () => {
    navigate('/campaigns/new');
  };
  const handleEditCampaign = (id: string) => {
    navigate(`/campaigns/edit/${id}`);
  };
  const handleViewCampaign = (campaign: Campaign) => {
    // For now, just show details in a modal or navigate to view page
    // navigate(`/campaigns/view/${campaign.id}`)
    setSelectedCampaign(campaign);
  };
  const handleApproveClick = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowApprovalModal(true);
  };
  const handleRejectClick = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowRejectionModal(true);
  };
  const confirmApproval = () => {
    if (!selectedCampaign) return;
    const updatedCampaigns = campaigns.map(c => c.id === selectedCampaign.id ? {
      ...c,
      status: 'active',
      approvedBy: 'current.user@example.com'
    } : c);
    setCampaigns(updatedCampaigns);
    setShowApprovalModal(false);
    setSelectedCampaign(null);
  };
  const confirmRejection = () => {
    if (!selectedCampaign) return;
    const updatedCampaigns = campaigns.map(c => c.id === selectedCampaign.id ? {
      ...c,
      status: 'rejected',
      rejectionReason: rejectionReason
    } : c);
    setCampaigns(updatedCampaigns);
    setShowRejectionModal(false);
    setRejectionReason('');
    setSelectedCampaign(null);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-3051" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending Approval" data-id="element-3052" />;
      case 'draft':
        return <StatusBadge status="default" text="Draft" data-id="element-3053" />;
      case 'rejected':
        return <StatusBadge status="danger" text="Rejected" data-id="element-3054" />;
      case 'expired':
        return <StatusBadge status="default" text="Expired" data-id="element-3055" />;
      case 'approved':
        return <StatusBadge status="info" text="Approved" data-id="element-3056" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-3057" />;
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0
    }).format(amount);
  };
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) || campaign.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter ? campaign.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });
  const columns = [{
    header: 'Campaign Name',
    accessor: (campaign: Campaign) => <div data-id="element-3058">
          <div className="font-medium text-gray-900 dark:text-white" data-id="element-3059">
            {campaign.name}
          </div>
          <div className="text-xs text-gray-500" data-id="element-3060">{campaign.code}</div>
        </div>,
    sortable: true
  }, {
    header: 'Status',
    accessor: (campaign: Campaign) => getStatusBadge(campaign.status),
    sortable: true
  }, {
    header: 'Discount',
    accessor: (campaign: Campaign) => <div data-id="element-3061">
          <span className="font-medium" data-id="element-3062">{campaign.value}</span>
          <span className="text-xs text-gray-500 ml-1" data-id="element-3063">({campaign.type})</span>
        </div>,
    sortable: true
  }, {
    header: 'Duration',
    accessor: (campaign: Campaign) => <div className="text-sm" data-id="element-3064">
          <div data-id="element-3065">{formatDate(campaign.startDate)}</div>
          <div className="text-xs text-gray-500" data-id="element-3066">
            to {formatDate(campaign.endDate)}
          </div>
        </div>,
    sortable: true
  }, {
    header: 'Budget Utilization',
    accessor: (campaign: Campaign) => {
      const percentage = Math.round(campaign.utilized / campaign.budget * 100);
      return <div className="w-32" data-id="element-3067">
            <div className="flex justify-between text-xs mb-1" data-id="element-3068">
              <span data-id="element-3069">{percentage}%</span>
              <span className="text-gray-500" data-id="element-3070">
                {formatCurrency(campaign.utilized)}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5" data-id="element-3071">
              <div className={`h-1.5 rounded-full ${percentage > 90 ? 'bg-red-500' : 'bg-blue-500'}`} style={{
            width: `${percentage}%`
          }} data-id="element-3072"></div>
            </div>
          </div>;
    },
    sortable: true
  }];
  return <div className="space-y-6" data-id="element-3073">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-3074">
        <div data-id="element-3075">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3076">
            Campaign Management
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-3077">
            Create and manage discount campaigns with maker-checker approval
            workflow
          </p>
        </div>
        <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-3078">
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-3079">
            <FilterIcon className="h-4 w-4 mr-2" data-id="element-3080" />
            Filters
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-3081">
            <DownloadIcon className="h-4 w-4 mr-2" data-id="element-3082" />
            Export
          </button>
          <button onClick={handleCreateCampaign} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-3083">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-3084" />
            New Campaign
          </button>
        </div>
      </div>

      {showFilters && <Card data-id="element-3085">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3086">
            <div data-id="element-3087">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3088">
                Search
              </label>
              <div className="relative" data-id="element-3089">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3090">
                  <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-3091" />
                </div>
                <input type="text" className="w-full pl-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Search by name or code..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-3092" />
              </div>
            </div>
            <div data-id="element-3093">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3094">
                Status
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-3095">
                <option value="" data-id="element-3096">All Statuses</option>
                <option value="active" data-id="element-3097">Active</option>
                <option value="pending" data-id="element-3098">Pending Approval</option>
                <option value="draft" data-id="element-3099">Draft</option>
                <option value="expired" data-id="element-3100">Expired</option>
                <option value="rejected" data-id="element-3101">Rejected</option>
              </select>
            </div>
            <div data-id="element-3102">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3103">
                Date Range
              </label>
              <input type="date" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-3104" />
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-3105">
            <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => {
          setSearchTerm('');
          setStatusFilter('');
        }} data-id="element-3106">
              Reset
            </button>
          </div>
        </Card>}

      <Card data-id="element-3107">
        <Table columns={columns} data={filteredCampaigns} keyField="id" actions={campaign => <div className="flex justify-end space-x-2" data-id="element-3109">
              <button onClick={() => handleViewCampaign(campaign)} className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="View Details" data-id="element-3110">
                <EyeIcon className="h-5 w-5" data-id="element-3111" />
              </button>

              {campaign.status === 'draft' && <button onClick={() => handleEditCampaign(campaign.id)} className="p-1 rounded-full text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30" title="Edit" data-id="element-3112">
                  <EditIcon className="h-5 w-5" data-id="element-3113" />
                </button>}

              {campaign.status === 'pending' && <>
                  <button onClick={() => handleApproveClick(campaign)} className="p-1 rounded-full text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30" title="Approve" data-id="element-3114">
                    <CheckCircleIcon className="h-5 w-5" data-id="element-3115" />
                  </button>
                  <button onClick={() => handleRejectClick(campaign)} className="p-1 rounded-full text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="Reject" data-id="element-3116">
                    <XCircleIcon className="h-5 w-5" data-id="element-3117" />
                  </button>
                </>}

              {['draft', 'rejected'].includes(campaign.status) && <button className="p-1 rounded-full text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="Delete" data-id="element-3118">
                  <TrashIcon className="h-5 w-5" data-id="element-3119" />
                </button>}
            </div>} data-id="element-3108" />
      </Card>

      {/* Approval Modal */}
      <Modal isOpen={showApprovalModal} onClose={() => setShowApprovalModal(false)} title="Approve Campaign" data-id="element-3120">
        <div className="space-y-4" data-id="element-3121">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4" data-id="element-3122">
            <div className="flex" data-id="element-3123">
              <div className="flex-shrink-0" data-id="element-3124">
                <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" data-id="element-3125" />
              </div>
              <div className="ml-3" data-id="element-3126">
                <h3 className="text-sm font-medium text-green-800 dark:text-green-300" data-id="element-3127">
                  Confirm Approval
                </h3>
                <div className="mt-2 text-sm text-green-700 dark:text-green-200" data-id="element-3128">
                  <p data-id="element-3129">
                    Are you sure you want to approve{' '}
                    <strong data-id="element-3130">{selectedCampaign?.name}</strong>? This will
                    activate the campaign based on its start date.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4" data-id="element-3131">
            <button onClick={() => setShowApprovalModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-3132">
              Cancel
            </button>
            <button onClick={confirmApproval} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700" data-id="element-3133">
              Approve Campaign
            </button>
          </div>
        </div>
      </Modal>

      {/* Rejection Modal */}
      <Modal isOpen={showRejectionModal} onClose={() => setShowRejectionModal(false)} title="Reject Campaign" data-id="element-3134">
        <div className="space-y-4" data-id="element-3135">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4" data-id="element-3136">
            <div className="flex" data-id="element-3137">
              <div className="flex-shrink-0" data-id="element-3138">
                <AlertCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" data-id="element-3139" />
              </div>
              <div className="ml-3" data-id="element-3140">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-300" data-id="element-3141">
                  Rejection Required
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-200" data-id="element-3142">
                  <p data-id="element-3143">
                    Please provide a reason for rejecting{' '}
                    <strong data-id="element-3144">{selectedCampaign?.name}</strong>. This will be sent
                    back to the maker for corrections.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-3145">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3146">
              Rejection Reason <span className="text-red-500" data-id="element-3147">*</span>
            </label>
            <textarea value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-red-500 focus:border-red-500" placeholder="Enter reason for rejection..." required data-id="element-3148" />
          </div>
          <div className="flex justify-end space-x-3 pt-4" data-id="element-3149">
            <button onClick={() => setShowRejectionModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-3150">
              Cancel
            </button>
            <button onClick={confirmRejection} disabled={!rejectionReason.trim()} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3151">
              Reject Campaign
            </button>
          </div>
        </div>
      </Modal>
    </div>;
};
export default CampaignList;